import { writesApi } from './api';
import type { Category, Brand } from '@apptypes/index';

// All admin catalog operations go to Spring Boot writes service

export const catalogService = {
  // ── Categories ──────────────────────────────────────────────────────────────

  listCategories: (scope: 'approved' | 'pending' | 'all' = 'all') =>
    writesApi.get<Category[]>('/admin/categories', { scope } as Record<string, unknown>),

  createCategory: (data: {
    name: string;
    description?: string;
    parentId?: string;
    imageUrl?: string;
    darkImageUrl?: string;
  }) => writesApi.post<Category>('/admin/categories', data),

  /**
   * Edit a category. Omitted fields are left alone; an EMPTY imageUrl removes
   * the icon, and the shops fall back to the one they draw from the name.
   */
  updateCategory: (
    categoryId: string,
    data: {
      name?: string;
      description?: string;
      imageUrl?: string;
      darkImageUrl?: string;
    },
  ) => writesApi.patch<Category>(`/admin/categories/${categoryId}`, data),

  /**
   * Presign → S3 PUT → public CloudFront URL, category `category_icon`
   * (S3 prefix categories/). The URL has to be public: the apps and the
   * website fetch it directly, with no session.
   */
  uploadCategoryIcon: async (file: File): Promise<string> => {
    const presigned = await writesApi.post<{ uploadUrl: string; publicUrl: string }>(
      '/uploads/presign',
      { fileName: file.name, contentType: file.type || 'image/png', category: 'category_icon' },
    );
    const put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/png' },
      body: file,
    });
    if (!put.ok) throw new Error('Icon upload failed');
    return presigned.publicUrl;
  },

  approveCategory: (categoryId: string) =>
    writesApi.patch(`/admin/categories/${categoryId}/approve`),

  rejectCategory: (categoryId: string, reason: string) =>
    writesApi.patch(`/admin/categories/${categoryId}/reject`, { rejectionReason: reason }),

  archiveCategory: (categoryId: string) =>
    writesApi.patch(`/admin/categories/${categoryId}/archive`),

  restoreCategory: (categoryId: string) =>
    writesApi.patch(`/admin/categories/${categoryId}/restore`),

  // ── Brands ──────────────────────────────────────────────────────────────────

  listBrands: (scope: 'approved' | 'pending' | 'all' = 'all') =>
    writesApi.get<Brand[]>('/admin/brands', { scope } as Record<string, unknown>),

  createBrand: (data: { name: string; description?: string; logoUrl?: string }) =>
    writesApi.post<Brand>('/admin/brands', data),

  approveBrand: (brandId: string) =>
    writesApi.patch(`/admin/brands/${brandId}/approve`),

  rejectBrand: (brandId: string, reason: string) =>
    writesApi.patch(`/admin/brands/${brandId}/reject`, { rejectionReason: reason }),

  archiveBrand: (brandId: string) =>
    writesApi.patch(`/admin/brands/${brandId}/archive`),

  restoreBrand: (brandId: string) =>
    writesApi.patch(`/admin/brands/${brandId}/restore`),
};
